import { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useParams } from "react-router";
import styles from "styles/neurofeedback-session.module.css";
import CircularProgress from "@mui/material/CircularProgress";
import LoadingButton from "@mui/lab/LoadingButton";
import DoneIcon from "@mui/icons-material/Done";
import SaveIcon from "@mui/icons-material/Save";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { RadioGroup, Radio } from "@mui/material";
import PatientService from "services/PatientService";
import EnrollmentService from "services/EnrollmentService.js";
import useAPI from "hooks/useAPI.js";
import { useOutletContext } from "react-router-dom";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";

function SessionNeurofeedback() {
    let navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isCompleted = searchParams.get("isCompleted");

    const [hospitalId] = useOutletContext();
    const { patientId } = useParams();
    const { enrollmentId } = useParams();
    const { sessionId } = useParams();
    const location = useLocation();
    const sessionNumber = location.state?.sessionNumber;
    const createdByUserFullName = location.state?.createdByUserFullName;


    const [sessionData, setSessionData] = useState({
        sessionDate: "",
        completedBy: "",
        hemisphereProgram: "",
        hemisphereNotes: "",
        currentMedications: "",
        parentUpdatesMedications: "",
        sleepHistory: "",
        parentUpdatesSleep: "",
        generalBehavior: "",
        parentUpdatesBehavior: "",
        location: "",
        capType: "",
        nfbProtocol: "",
        activeSites: [],
        wetCapColor: [],
        tensUnitLocations: [],
        eyeLightsColor: [],
        laserHemisphereProtocol: "",
        brodmannAreas: [],
        broadmannAreasNotes: "",
        laserDurationNotes: "",
        laserFrequencyMin: 0,
        laserFrequencyMax: 0,
        laserDuration: "",
        laserTiming: "",
        nfbSessionTime: "",
        laserSessionTime: "",
        networksTargeted: [],
        parentPresent: "",
        parentNotes: "",
        behaviors: [],
        sessionCompletion: "",
        additionalNotes: "",
        progressLevel: "",
    });

    const [patientInfo, setPatientInfo] = useState({
        name: "",
        dob: "",
    });

    const getPatientInfoAPI = useAPI(
        PatientService.getPatient,
        getPatientInfoHandler
    );
    const getSessionDataAPI = useAPI(
        EnrollmentService.getSessionNeurofeedback,
        getSessionDataHandler
    );
    const saveSessionAPI = useAPI(EnrollmentService.saveSessionNeurofeedback, () => { });
    const submitSessionAPI = useAPI(
        EnrollmentService.submitEnrollmentSession,
        submitSessionHandler,
        true
    );

    function getPatientInfo() {
        getPatientInfoAPI.execute(hospitalId, patientId);
    }

    function getSessionData() {
        getSessionDataAPI.execute(hospitalId, patientId, enrollmentId, sessionId);
    }

    function saveSession() {

        saveSessionAPI.execute(hospitalId, patientId, enrollmentId, sessionId, { formData: sessionData });
    }

    function submitSession() {
        saveSession();
        submitSessionAPI.execute(hospitalId, patientId, enrollmentId, sessionId, sessionData);
    }

    useEffect(() => {
        getPatientInfo();
        getSessionData();
    }, []);
    useEffect(() => {
        // Clear active sites when cap type changes
        setSessionData(prev => ({
            ...prev,
            activeSites: []
        }));
    }, [sessionData.capType]);

    /* API Success Handlers */
    function getPatientInfoHandler(response) {
        setPatientInfo({
            name: response.data.firstName + " " + response.data.lastName,
            dob: response.data.dob,
        });
    }

    function getSessionDataHandler(response) {
        const data = response.data;
        const formData = data.formData ? JSON.parse(data.formData) : {};

        if (formData && Object.keys(formData).length > 0) {
            setSessionData(prev => ({
                ...prev,
                ...formData,
                sessionDate: formData.sessionDate || (data.createdAt ? data.createdAt.split('T')[0] : prev.sessionDate),
                sessionDateFromAPI: !!formData.sessionDate,
            }));
        } else {
            const defaultDate = data.createdAt ? data.createdAt.split('T')[0] : new Date().toISOString().split("T")[0];
            setSessionData(prev => ({
                ...prev,
                completedBy: createdByUserFullName,
                sessionDate: defaultDate,
            }));
        }
    }


    function submitSessionHandler(response) {
        navigate(-1);
    }

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (type === "checkbox") {
            if (name.includes("[]")) {
                // Multi-select checkboxes (arrays)
                const fieldName = name.replace("[]", "");
                setSessionData(prev => {
                    const currentArray = prev[fieldName] || [];
                    const newArray = checked
                        ? [...currentArray, value]
                        : currentArray.filter(item => item !== value);
                    return { ...prev, [fieldName]: newArray };
                });
            } else {
                // Single-select checkboxes (radio button behavior)
                setSessionData(prev => {
                    const currentValue = prev[name];
                    // If the same value is clicked, toggle it off, otherwise set the new value
                    const newValue = currentValue === value ? "" : value;
                    return { ...prev, [name]: newValue };
                });
            }
        } else {
            setSessionData(prev => ({ ...prev, [name]: value }));
        }
    };

    const brodmannAreas = [
        "BA 1", "BA 2", "BA 3", "BA 4", "BA 5", "BA 6", "BA 7", "BA 8", "BA 9", "BA 10",
        "BA 11", "BA 12", "BA 13", "BA 14", "BA 15", "BA 16", "BA 17", "BA 18", "BA 19", "BA 20",
        "BA 21", "BA 22", "BA 23", "BA 24", "BA 25", "BA 26", "BA 27", "BA 28", "BA 29", "BA 30",
        "BA 31", "BA 32", "BA 33", "BA 34", "BA 35", "BA 36", "BA 37", "BA 38", "BA 39", "BA 40",
        "BA 41", "BA 42", "BA 43", "BA 44", "BA 45", "BA 46", "BA 47"
    ];

    const activeSites = [
        "Fp1", "Fp2", "F3", "F4", "Fpz", "Fz", "F7", "F8", "C3", "C4", "Cz",
        "T3", "T4", "T5", "T6", "P3", "P4", "Pz", "O1", "O2", "Oz"
    ];

    const behaviors = [
        "CNP", "Lack of Focus", "Focused", "Inattentive", "Redirection Given",
        "Non-responsive to redirection", "Dysregulated", "Tried to engage socially throughout session",
        "Good eye contact", "Self-initiated appropriate social interaction", "Stim (physical/vocal)",
        "Breaks needed", "Eloped session (left room)", "Confident", "Frequent movement during recording",
        "Crying", "Fear Paralysis", "Tired through session", "Yawning frequently", "Fatigued before session"
    ];

    const durationOptions = [
        "0–5 min", "5–10 min", "10–15 min", "15–20 min", "20–25 min",
        "25–30 min", "30–35 min", "35–40 min", "40–45 min", "CNP"
    ];


    // State to control "Saved" message visibility
    const [showSaved, setShowSaved] = useState(false);

    // Effect to auto-hide the "Saved" message after 2 seconds
    useEffect(() => {
        if (saveSessionAPI.status === "success") {
            setShowSaved(true);

            const timer = setTimeout(() => setShowSaved(false), 2000);
            return () => clearTimeout(timer);
        }
    }, [saveSessionAPI.status]);
    useEffect(() => {
        // Only set if completedBy is empty and we have createdByUserFullName
        if (createdByUserFullName && !sessionData.completedBy) {
            setSessionData(prev => ({
                ...prev,
                completedBy: createdByUserFullName
            }));
        }
    }, [createdByUserFullName]);

    return (
        <Paper elevation={0} className="p-4">
            <div className="d-flex justify-content-between">
                <Typography variant="h2" gutterBottom>
                    Neurofeedback Session{sessionNumber ? ` #${sessionNumber}` : ''}
                </Typography>
                <Button onClick={() => navigate(-1)} variant="outlined">
                    Back
                </Button>
            </div>

            <form>
                {getPatientInfoAPI.status === "pending" || getSessionDataAPI.status === "pending" ? (
                    <div className="text-center">
                        <CircularProgress className="text-center" sx={{ my: 20 }} />
                    </div>
                ) : (
                    <div className="mt-4">
                        {/* Patient Info Section */}
                        <Paper elevation={1} className="p-4 mb-4">
                            <Typography variant="h3" mb={6}>Patient Information</Typography>
                            <div className="row mb-3">
                                <div className="col-md-4">
                                    <TextField
                                        label="Patient Name"
                                        value={patientInfo.name}
                                        fullWidth
                                        InputProps={{ readOnly: true }}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <TextField
                                        label="Date of Birth"
                                        value={patientInfo.dob}
                                        fullWidth
                                        InputProps={{ readOnly: true }}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <TextField
                                        label="Date of Session"
                                        name="sessionDate"
                                        type="date"
                                        value={sessionData.sessionDate}
                                        onChange={(e) =>
                                            setSessionData((prev) => ({
                                                ...prev,
                                                sessionDate: e.target.value,
                                            }))
                                        }
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{
                                            readOnly: !!getSessionDataAPI.data?.data?.formData && !!sessionData.sessionDate,
                                        }}
                                    />
                                </div>
                            </div>

                            <Typography variant="subtitle1" gutterBottom>NFB Completed By</Typography>
                            <TextField
                                label="Completed By"
                                name="completedBy"
                                value={sessionData.completedBy || createdByUserFullName}
                                onChange={handleInputChange}
                                fullWidth
                                sx={{ mt: 3 }}
                            />
                        </Paper>
                        <Paper elevation={1} className="p-4 mb-4">
                            <Typography variant="h3" mb={6}>Transferred Patient Data</Typography>

                            <Typography variant="subtitle1" gutterBottom>Right or Left Hemisphere Program</Typography>
                            <Stack direction="row" spacing={2}>
                                <FormControl>
                                    <RadioGroup
                                        row
                                        name="hemisphereProgram"
                                        value={sessionData.hemisphereProgram}
                                        onChange={handleInputChange}
                                    >
                                        <FormControlLabel
                                            value="Right"
                                            control={<Radio />}
                                            label="Right Hemisphere Program"
                                        />
                                        <FormControlLabel
                                            value="Left"
                                            control={<Radio />}
                                            label="Left Hemisphere Program"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Stack>

                            <TextField
                                label="Notes/Updates"
                                name="hemisphereNotes"
                                value={sessionData.hemisphereNotes}
                                onChange={handleInputChange}
                                fullWidth
                                multiline
                                rows={2}
                                className="mt-3"
                            />

                            <Typography variant="subtitle1" gutterBottom className="mt-3">Current Medications</Typography>
                            <TextField
                                label="Current Medications"
                                name="currentMedications"
                                value={sessionData.currentMedications}
                                onChange={handleInputChange}
                                fullWidth
                                multiline
                                rows={2}
                                sx={{ mt: 2 }}
                            />
                            <TextField
                                label="Patient/Caregiver Updates"
                                name="parentUpdatesMedications"
                                value={sessionData.parentUpdatesMedications}
                                onChange={handleInputChange}
                                fullWidth
                                multiline
                                rows={2}
                                sx={{ mt: 3 }}
                            />

                            <Typography variant="subtitle1" gutterBottom className="mt-3">Sleep History</Typography>
                            <TextField
                                label="Sleep History"
                                name="sleepHistory"
                                value={sessionData.sleepHistory}
                                onChange={handleInputChange}
                                fullWidth
                                multiline
                                rows={2}
                                sx={{ mt: 2 }}
                            />
                            <TextField
                                label="Patient Updates (if applicable)"
                                name="parentUpdatesSleep"
                                value={sessionData.parentUpdatesSleep}
                                onChange={handleInputChange}
                                fullWidth
                                multiline
                                rows={2}
                                sx={{ mt: 3 }}
                            />

                            <Typography variant="subtitle1" gutterBottom className="mt-3">General Behavior</Typography>
                            <TextField
                                label="General Behavior"
                                name="generalBehavior"
                                value={sessionData.generalBehavior}
                                onChange={handleInputChange}
                                fullWidth
                                multiline
                                rows={2}
                                sx={{ mt: 2 }}
                            />
                            <TextField
                                label="Patient Updates (if applicable)"
                                name="parentUpdatesBehavior"
                                value={sessionData.parentUpdatesBehavior}
                                onChange={handleInputChange}
                                fullWidth
                                multiline
                                rows={2}
                                sx={{ mt: 3 }}
                            />
                        </Paper>

                        {/* Session-Specific Section */}
                        <Paper elevation={1} className="p-4 mb-4">
                            <Typography variant="h3" mb={6}>Session-Specific</Typography>

                            <Typography variant="subtitle1" gutterBottom>Location of Session</Typography>
                            <Stack direction="row" spacing={2}>
                                <FormControl component="fieldset">
                                    <RadioGroup
                                        row
                                        name="location"
                                        value={sessionData.location}
                                        onChange={handleInputChange}
                                    >
                                        <FormControlLabel
                                            value="NYC Office"
                                            control={<Radio />}
                                            label="NYC Office"
                                        />
                                        <FormControlLabel
                                            value="Rockville Centre Office"
                                            control={<Radio />}
                                            label="Rockville Centre Office"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Stack>

                            <Typography variant="subtitle1" gutterBottom className="mt-3">Cap/Sensor Type Used</Typography>
                            <Stack direction="row" spacing={2}>
                                <FormControl component="fieldset">
                                    <RadioGroup
                                        row
                                        name="capType"
                                        value={sessionData.capType}
                                        onChange={handleInputChange}
                                    >
                                        <FormControlLabel
                                            value="Dry Cap"
                                            control={<Radio />
                                            }
                                            label="Dry Cap"
                                        />
                                        <FormControlLabel
                                            value="Wet Cap"
                                            control={<Radio />}
                                            label="Wet Cap"
                                        />
                                        <FormControlLabel
                                            value="Single Channel Sensors"
                                            control={<Radio />}
                                            label="Single Channel Sensors"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Stack>

                            <TextField
                                label="NFB Protocol"
                                name="nfbProtocol"
                                value={sessionData.nfbProtocol}
                                onChange={handleInputChange}
                                fullWidth
                                className="mt-3"
                            />

                            {/* Show active sites for ALL cap types */}
                            {(sessionData.capType === "Single Channel Sensors" ||
                                sessionData.capType === "Wet Cap" ||
                                sessionData.capType === "Dry Cap") && (
                                    <div className="mt-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <Typography variant="subtitle1" gutterBottom>
                                                Active Sites
                                            </Typography>
                                            <Button
                                                size="small"
                                                onClick={() => {
                                                    const allSelected = activeSites.every(site =>
                                                        sessionData.activeSites?.includes(site)
                                                    );

                                                    setSessionData(prev => ({
                                                        ...prev,
                                                        activeSites: allSelected ? [] : [...activeSites]
                                                    }));
                                                }}
                                            >
                                                {sessionData.activeSites?.length === activeSites.length ? "Deselect All" : "Select All"}
                                            </Button>
                                        </div>

                                        <div className={styles["checkbox-grid"]}>
                                            {activeSites.map(site => (
                                                <FormControlLabel
                                                    key={site}
                                                    control={
                                                        <Checkbox
                                                            name="activeSites[]"
                                                            value={site}
                                                            checked={sessionData.activeSites?.includes(site)}
                                                            onChange={handleInputChange}
                                                        />
                                                    }
                                                    label={site}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}

                            {sessionData.capType === "Wet Cap" && (
                                <div className="mt-3">

                                    <Typography variant="subtitle2" className="mt-3">Cap Colors:</Typography>
                                    <Stack direction="row" spacing={2} flexWrap="wrap">
                                        {["Blue", "Red", "Blue/Red", "Red/Yellow", "Yellow", "Green"].map(color => (
                                            <FormControlLabel
                                                key={color}
                                                control={
                                                    <Checkbox
                                                        name="wetCapColor[]"
                                                        value={color}
                                                        checked={sessionData.wetCapColor?.includes(color)}
                                                        onChange={handleInputChange}
                                                    />
                                                }
                                                label={color}
                                            />
                                        ))}
                                    </Stack>
                                </div>
                            )}

                            <Typography variant="subtitle1" gutterBottom className="mt-3">Additional Devices Used</Typography>

                            <Typography variant="subtitle2" gutterBottom>TENS Unit</Typography>
                            <Stack direction="row" spacing={2}>
                                {["Left Upper Back", "Right Upper Back", "Left Lower Back", "Right Lower Back"].map(location => (
                                    <FormControlLabel
                                        key={location}
                                        control={
                                            <Checkbox
                                                name="tensUnitLocations[]"
                                                value={location}
                                                checked={sessionData.tensUnitLocations?.includes(location)}
                                                onChange={handleInputChange}
                                            />
                                        }
                                        label={location}
                                    />
                                ))}
                            </Stack>

                            <Typography variant="subtitle2">Eye Lights</Typography>
                            <Stack direction="row" spacing={2}>
                                {["Red", "Blue"].map(color => (
                                    <FormControlLabel
                                        key={color}
                                        control={
                                            <Checkbox
                                                name="eyeLightsColor[]"
                                                value={color}
                                                checked={sessionData.eyeLightsColor?.includes(color)}
                                                onChange={handleInputChange}
                                            />
                                        }
                                        label={color}
                                    />
                                ))}
                            </Stack>

                            <Typography variant="subtitle1" gutterBottom className="mt-3">Laser Therapy</Typography>

                            <Typography variant="subtitle2" gutterBottom>Hemispheric Protocol Focus</Typography>
                            <Stack direction="row" spacing={2}>
                                <FormControl component="fieldset">
                                    <RadioGroup
                                        row
                                        name="laserHemisphereProtocol"
                                        value={sessionData.laserHemisphereProtocol}
                                        onChange={handleInputChange}
                                    >
                                        <FormControlLabel
                                            value="Left Hemisphere Protocol"
                                            control={<Radio />
                                            }
                                            label="Left Hemisphere Protocol"
                                        />
                                        <FormControlLabel
                                            value="Right Hemisphere Protocol"
                                            control={<Radio />}
                                            label="Right Hemisphere Protocol"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Stack>

                            <Typography variant="subtitle2" mt={6}>Targeted Brodmann Areas</Typography>
                            <div className={styles["checkbox-grid"]}>
                                {brodmannAreas.map(area => (
                                    <FormControlLabel
                                        key={area}
                                        control={
                                            <Checkbox
                                                name="brodmannAreas[]"
                                                value={area}
                                                checked={sessionData.brodmannAreas?.includes(area)}
                                                onChange={handleInputChange}
                                            />
                                        }
                                        label={area}
                                    />
                                ))}
                            </div>

                            <TextField
                                label="Notes"
                                name="broadmannAreasNotes"
                                value={sessionData.broadmannAreasNotes}
                                onChange={handleInputChange}
                                fullWidth
                                multiline
                                rows={2}
                                className="mt-2"
                            />

                            <Typography variant="subtitle2" mt={6} mb={4}>Laser Frequency Range Used</Typography>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <FormControl fullWidth sx={{ maxWidth: 120 }}>
                                    <InputLabel>Min</InputLabel>
                                    <Select
                                        name="laserFrequencyMin"
                                        value={sessionData.laserFrequencyMin}
                                        onChange={handleInputChange}
                                        label="Min"
                                    >
                                        {Array.from({ length: 46 }, (_, i) => (
                                            <MenuItem key={i} value={i}>{i}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Typography variant="body1">to</Typography>
                                <FormControl fullWidth sx={{ maxWidth: 120 }}>
                                    <InputLabel>Max</InputLabel>
                                    <Select
                                        name="laserFrequencyMax"
                                        value={sessionData.laserFrequencyMax}
                                        onChange={handleInputChange}
                                        label="Max"
                                    >
                                        {Array.from({ length: 46 }, (_, i) => (
                                            <MenuItem key={i} value={i}>{i}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Stack>

                            <Typography variant="subtitle2" mt={6}>Laser Duration</Typography>
                            <Stack direction="row" spacing={2} flexWrap="wrap">
                                <FormControl>
                                    <RadioGroup
                                        row
                                        name="laserDuration"
                                        value={sessionData.laserDuration}
                                        onChange={handleInputChange}
                                    >
                                        {durationOptions.map(option => (
                                            <FormControlLabel
                                                key={option}
                                                value={option}
                                                control={<Radio />}
                                                label={option}
                                            />
                                        ))}
                                    </RadioGroup>
                                </FormControl>
                            </Stack>

                            <TextField
                                label="Notes/Updates"
                                name="laserDurationNotes"
                                value={sessionData.laserDurationNotes}
                                onChange={handleInputChange}
                                fullWidth
                                multiline
                                rows={2}
                                className="mt-2"
                            />

                            <Typography variant="subtitle2" mt={6}>Timing of Laser Therapy Relative to Neurofeedback</Typography>
                            <Stack direction="row" spacing={2}>
                                <FormControl component="fieldset">
                                    <RadioGroup
                                        row
                                        name="laserTiming"
                                        value={sessionData.laserTiming}
                                        onChange={handleInputChange}
                                    >
                                        {["Before NFB", "With NFB", "After NFB", "Before & After NFB"].map(option => (
                                            <FormControlLabel
                                                key={option}
                                                value={option}
                                                control={<Radio />}
                                                label={option}
                                            />
                                        ))}
                                    </RadioGroup>
                                </FormControl>
                            </Stack>
                        </Paper>

                        {/* Session Timing and Focus Section */}
                        <Paper elevation={1} className="p-4 mb-4">
                            <Typography variant="h3" mb={6}>Session Timing and Focus</Typography>

                            <Typography variant="subtitle1" gutterBottom className="mt-3">Session Timing</Typography>

                            <div className="row">
                                <div className="col-md-6">
                                    <Typography variant="subtitle2" gutterBottom>Neurofeedback Session Duration</Typography>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <FormControl fullWidth sx={{ maxWidth: 150 }}>
                                            <InputLabel>Duration</InputLabel>
                                            <Select
                                                name="nfbSessionTime"
                                                value={sessionData.nfbSessionTime}
                                                onChange={handleInputChange}
                                                label="Duration"
                                            >
                                                <MenuItem value="0">0 min</MenuItem>
                                                <MenuItem value="5">5 min</MenuItem>
                                                <MenuItem value="10">10 min</MenuItem>
                                                <MenuItem value="15">15 min</MenuItem>
                                                <MenuItem value="20">20 min</MenuItem>
                                                <MenuItem value="25">25 min</MenuItem>
                                                <MenuItem value="30">30 min</MenuItem>
                                                <MenuItem value="35">35 min</MenuItem>
                                                <MenuItem value="40">40 min</MenuItem>
                                                <MenuItem value="45">45 min</MenuItem>
                                                <MenuItem value="custom">Custom</MenuItem>
                                            </Select>
                                        </FormControl>

                                        {sessionData.nfbSessionTime === "custom" && (
                                            <TextField
                                                label="Custom Duration (min)"
                                                name="customNfbTime"
                                                type="number"
                                                value={sessionData.customNfbTime || ''}
                                                onChange={handleInputChange}
                                                sx={{ maxWidth: 120 }}
                                            />
                                        )}
                                    </Stack>
                                </div>

                                <div className="col-md-6">
                                    <Typography variant="subtitle2" gutterBottom>Laser Session Duration</Typography>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <FormControl fullWidth sx={{ maxWidth: 150 }}>
                                            <InputLabel>Duration</InputLabel>
                                            <Select
                                                name="laserSessionTime"
                                                value={sessionData.laserSessionTime}
                                                onChange={handleInputChange}
                                                label="Duration"
                                            >
                                                <MenuItem value="0">0 min</MenuItem>
                                                <MenuItem value="5">5 min</MenuItem>
                                                <MenuItem value="10">10 min</MenuItem>
                                                <MenuItem value="15">15 min</MenuItem>
                                                <MenuItem value="20">20 min</MenuItem>
                                                <MenuItem value="25">25 min</MenuItem>
                                                <MenuItem value="30">30 min</MenuItem>
                                                <MenuItem value="custom">Custom</MenuItem>
                                            </Select>
                                        </FormControl>

                                        {sessionData.laserSessionTime === "custom" && (
                                            <TextField
                                                label="Custom Duration (min)"
                                                name="customLaserTime"
                                                type="number"
                                                value={sessionData.customLaserTime || ''}
                                                onChange={handleInputChange}
                                                sx={{ maxWidth: 120 }}
                                            />
                                        )}
                                    </Stack>
                                </div>
                            </div>

                            <Typography variant="subtitle1" mt={6}>Networks Targeted During NFB</Typography>
                            <Stack direction="row" spacing={2}>
                                {["DMN (Default Mode Network)", "SMN (Sensorimotor Network)", "CEN (Central Executive Network)"].map(network => (
                                    <FormControlLabel
                                        key={network}
                                        control={
                                            <Checkbox
                                                name="networksTargeted[]"
                                                value={network}
                                                checked={sessionData.networksTargeted?.includes(network)}
                                                onChange={handleInputChange}
                                            />
                                        }
                                        label={network}
                                    />
                                ))}
                            </Stack>

                            <Typography variant="subtitle1" mt={6}>Parent Present During Session</Typography>
                            <Stack direction="row" spacing={2}>
                                <FormControl component="fieldset">
                                    <RadioGroup
                                        row
                                        name="parentPresent"
                                        value={sessionData.parentPresent}
                                        onChange={handleInputChange}
                                    >
                                        {["Yes", "No", "N/A (Adult)"].map(option => (
                                            <FormControlLabel
                                                key={`parent-${option}`}
                                                value={option}
                                                control={<Radio />}
                                                label={option}
                                            />
                                        ))}
                                    </RadioGroup>
                                </FormControl>
                            </Stack>

                            <TextField
                                label="Notes (optional)"
                                name="parentNotes"
                                value={sessionData.parentNotes}
                                onChange={handleInputChange}
                                fullWidth
                                multiline
                                rows={2}
                                className="mt-2"
                            />

                            <Typography variant="subtitle1" mt={6}>Behavioral Input (Multi-Select)</Typography>
                            <div className={styles["checkbox-grid"]}>
                                {behaviors.map(behavior => (
                                    <FormControlLabel
                                        key={behavior}
                                        control={
                                            <Checkbox
                                                name="behaviors[]"
                                                value={behavior}
                                                checked={sessionData.behaviors?.includes(behavior)}
                                                onChange={handleInputChange}
                                            />
                                        }
                                        label={behavior}
                                    />
                                ))}
                            </div>

                            <Typography variant="subtitle1" mt={6}>Completion of Neurofeedback Session</Typography>
                            <Stack direction="row" spacing={2}>
                                <FormControl component="fieldset">
                                    <RadioGroup
                                        row
                                        name="sessionCompletion"
                                        value={sessionData.sessionCompletion}
                                        onChange={handleInputChange}
                                    >
                                        {["Yes", "CNP", "Attempted but unable will retry in future"].map(option => (
                                            <FormControlLabel
                                                key={`completion-${option}`}
                                                value={option}
                                                control={<Radio />}
                                                label={option}
                                            />
                                        ))}
                                    </RadioGroup>
                                </FormControl>
                            </Stack>

                            <TextField
                                label="Additional Notes"
                                name="additionalNotes"
                                value={sessionData.additionalNotes}
                                onChange={handleInputChange}
                                fullWidth
                                multiline
                                rows={3}
                                sx={{ mt: 3 }}
                            />

                            <Typography variant="subtitle1" mt={6}>Level of Progress (1–10)</Typography>
                            <Stack direction="row" spacing={2} flexWrap="wrap">
                                <FormControl component="fieldset">
                                    <RadioGroup
                                        row
                                        name="progressLevel"
                                        value={sessionData.progressLevel}
                                        onChange={handleInputChange}
                                    >
                                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(option => (
                                            <FormControlLabel
                                                key={`progress-${option}`}
                                                value={option}
                                                control={<Radio />}
                                                label={option}
                                            />
                                        ))}
                                    </RadioGroup>
                                </FormControl>
                            </Stack>
                        </Paper>

                        {/* Submit Button */}
                        {isCompleted === "false" && (
                            <div className="d-flex align-items-center mb-3">
                                <LoadingButton
                                    loading={saveSessionAPI.status === "pending"}
                                    loadingPosition="start"
                                    startIcon={<SaveIcon />}
                                    variant="outlined"
                                    className="me-3"
                                    onClick={saveSession}
                                    type="button"
                                >
                                    Save
                                </LoadingButton>
                                {submitSessionAPI.status === "pending" ? (
                                    <LoadingButton
                                        loading
                                        loadingPosition="start"
                                        startIcon={<SaveIcon />}
                                        variant="contained"
                                        className="me-3"
                                    >
                                        Submit
                                    </LoadingButton>
                                ) : (
                                    <Button
                                        type="submit"
                                        className="me-3"
                                        startIcon={<DoneIcon />}
                                        variant="contained"
                                        onClick={submitSession}
                                    >
                                        Submit
                                    </Button>
                                )}
                                {showSaved && (
                                    <Typography color="success.main">Saved</Typography>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </form>
        </Paper >
    );
}

export default SessionNeurofeedback;
